import React, { useMemo, useState, useCallback, useRef, useEffect } from "react";
import { Box, Typography, Menu, MenuItem, ListItemIcon, ListItemText } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import {
    format,
    addDays,
    startOfWeek,
    startOfDay,
    parseISO,
    isSameDay
} from "date-fns";
import CalendarEvent from "./CalendarEvent";
import CalendarTooltip from "./CalendarTooltip";

const HOUR_START = 0; // midnight
const HOUR_END = 24; // midnight

/**
 * Formats a timezone for display.
 * Returns e.g. "EDT · Eastern Daylight Time"
 */
const formatTimezoneDisplay = (tz) => {
    if (!tz) return "";
    try {
        const now = new Date();
        const shortFmt = new Intl.DateTimeFormat("en-US", {
            timeZone: tz,
            timeZoneName: "short"
        });
        const longFmt = new Intl.DateTimeFormat("en-US", {
            timeZone: tz,
            timeZoneName: "long"
        });
        const shortName =
            shortFmt.formatToParts(now).find((p) => p.type === "timeZoneName")
                ?.value || "";
        const longName =
            longFmt.formatToParts(now).find((p) => p.type === "timeZoneName")
                ?.value || tz;
        return shortName ? `${shortName} · ${longName}` : longName;
    } catch {
        return tz;
    }
};

// Colors for per-provider timezone strips
const TZ_STRIP_COLORS = [
    { bg: "#E3F2FD", text: "#1565C0", border: "#BBDEFB" },
    { bg: "#F3E5F5", text: "#7B1FA2", border: "#CE93D8" },
    { bg: "#E8F5E9", text: "#2E7D32", border: "#A5D6A7" },
    { bg: "#FFF3E0", text: "#E65100", border: "#FFCC80" },
    { bg: "#FCE4EC", text: "#C62828", border: "#EF9A9A" }
];

/**
 * Checks if an event is an all-day event (spans 00:00 to 23:59 or similar).
 */
const isAllDayEvent = (event) => {
    const start = event.startTime || "";
    const end = event.endTime || "";
    if (start === "00:00" && (end === "23:59" || end === "00:00" || end === "23:00")) return true;
    // Also treat events with no start/end time as all-day
    if (!start && !end) return true;
    // Check if duration spans 12+ hours
    if (start && end) {
        const [sh, sm] = start.split(":").map(Number);
        const [eh, em] = end.split(":").map(Number);
        const durationMins = (eh * 60 + em) - (sh * 60 + sm);
        if (durationMins >= 720) return true; // 12+ hours
    }
    return false;
};
const TIME_LABEL_WIDTH = 60;
const TZ_STRIP_HEIGHT = 22;
const PROVIDER_NAME_HEIGHT = 30;
const DAY_HEADER_HEIGHT = 28;
const HEADER_HEIGHT = PROVIDER_NAME_HEIGHT + TZ_STRIP_HEIGHT + DAY_HEADER_HEIGHT;

/**
 * Builds a Set of "YYYY-MM-DD|HH:MM" keys from the flat availability array
 * for O(1) lookup per cell.
 */
const buildAvailabilitySet = (availability, cellDuration) => {
    const set = new Set();
    if (!availability) return set;

    for (const slot of availability) {
        const { date, startTime, endTime } = slot;
        if (!date || !startTime || !endTime) continue;

        const [startH, startM] = startTime.split(":").map(Number);
        const [endH, endM] = endTime.split(":").map(Number);
        const startMins = startH * 60 + startM;
        const endMins = endH * 60 + endM;

        // Snap the first cell down to the grid boundary (multiples of
        // cellDuration from midnight) so the generated keys align with the
        // grid's time slots. Without this, an availability window that starts
        // off-grid (e.g. 11:30 with 60-min cells) produces keys on the :30
        // that never match the on-the-hour slot keys, so the column renders
        // as fully unavailable. A cell is marked available if any part of it
        // overlaps the availability window.
        const firstCell = Math.floor(startMins / cellDuration) * cellDuration;
        for (let m = firstCell; m < endMins; m += cellDuration) {
            const h = String(Math.floor(m / 60)).padStart(2, "0");
            const min = String(m % 60).padStart(2, "0");
            set.add(`${date}|${h}:${min}`);
        }
    }
    return set;
};

/**
 * Compute event positions (top offset and height) relative to the grid.
 */
const computeEventPosition = (event, cellDuration, cellHeight) => {
    const [startH, startM] = (event.startTime || "00:00")
        .split(":")
        .map(Number);
    const [endH, endM] = (event.endTime || "00:00").split(":").map(Number);

    const startMins = startH * 60 + startM;
    const endMins =
        endH * 60 + endM > startMins ? endH * 60 + endM : startMins + 30;

    const topMins = startMins - HOUR_START * 60;
    const durationMins = endMins - startMins;

    const pixelsPerMin = cellHeight / cellDuration;
    return {
        topOffset: Math.max(0, topMins * pixelsPerMin),
        height: Math.max(20, durationMins * pixelsPerMin - 2),
        startMins,
        endMins
    };
};

/**
 * Assigns column layout to overlapping events (Google Calendar algorithm).
 * Returns events with added `col` (0-based column index) and `totalCols`.
 */
const layoutOverlappingEvents = (events, cellDuration, cellHeight) => {
    if (!events?.length) return [];

    // Compute positions and sort by start time
    const items = events.map((evt) => ({
        evt,
        pos: computeEventPosition(evt, cellDuration, cellHeight)
    }));
    items.sort((a, b) => a.pos.startMins - b.pos.startMins);

    // Greedy column assignment
    const columns = []; // each column tracks the end time of its last event

    for (const item of items) {
        // Find the first column where this event doesn't overlap
        let placed = false;
        for (let c = 0; c < columns.length; c++) {
            if (item.pos.startMins >= columns[c]) {
                columns[c] = item.pos.endMins;
                item.col = c;
                placed = true;
                break;
            }
        }
        if (!placed) {
            item.col = columns.length;
            columns.push(item.pos.endMins);
        }
    }

    // Now determine totalCols for each overlap group.
    // Two events are in the same group if they transitively overlap.
    // We do a second pass: for each event, find all events that overlap with it
    // and take the max column count among the connected group.
    const totalCols = columns.length;

    // For better visual density, compute per-group column counts
    // by finding connected components of overlapping events.
    const groups = [];
    let currentGroup = [];

    for (const item of items) {
        if (
            currentGroup.length === 0 ||
            item.pos.startMins <
                Math.max(...currentGroup.map((i) => i.pos.endMins))
        ) {
            currentGroup.push(item);
        } else {
            groups.push(currentGroup);
            currentGroup = [item];
        }
    }
    if (currentGroup.length) groups.push(currentGroup);

    // Assign totalCols per group
    for (const group of groups) {
        const maxCol = Math.max(...group.map((i) => i.col)) + 1;
        for (const item of group) {
            item.totalCols = maxCol;
        }
    }

    return items;
};

/**
 * CalendarGrid - The core calendar rendering component.
 *
 * Uses CSS Grid with:
 * - Columns: time labels + (days * providers)
 * - Rows: time slots based on cellDuration
 */
const CalendarGrid = React.memo(function CalendarGrid({
    calendarData,
    providers,
    anchorDate,
    view,
    cellDuration,
    showSaturday = true,
    showSunday = true,
    onCellClick,
    onEventEdit,
    onEventDelete,
    canAddBooking = true
}) {
    const scrollContainerRef = useRef(null);
    const hoverTimerRef = useRef(null);
    useEffect(() => () => clearTimeout(hoverTimerRef.current), []);
    const cellHeight = cellDuration === 15 ? 25 : cellDuration === 30 ? 40 : 80;
    const [tooltipState, setTooltipState] = useState({
        anchorEl: null,
        event: null,
        type: null,
        pinned: false // true = opened by click (stays open), false = opened by hover (closes on leave)
    });

    // Auto-scroll to current time on mount
    useEffect(() => {
        if (scrollContainerRef.current) {
            const now = new Date();
            const currentMins = now.getHours() * 60 + now.getMinutes();
            const pixelsPerMin = cellHeight / cellDuration;
            // Scroll to ~1 hour before current time
            const scrollTo = Math.max(0, (currentMins - 60) * pixelsPerMin);
            scrollContainerRef.current.scrollTop = scrollTo;
        }
    }, [cellHeight, cellDuration]);

    // Compute the days to show, filtering out hidden weekend days
    const days = useMemo(() => {
        if (view === "Day") {
            return [startOfDay(anchorDate)];
        }
        const weekStart = startOfWeek(anchorDate, { weekStartsOn: 1 });
        const allDays = Array.from({ length: 7 }, (_, i) =>
            addDays(weekStart, i)
        );
        return allDays.filter((day) => {
            const dayOfWeek = day.getDay();
            if (dayOfWeek === 6 && !showSaturday) return false;
            if (dayOfWeek === 0 && !showSunday) return false;
            return true;
        });
    }, [anchorDate, view, showSaturday, showSunday]);

    // Time slots for the left column
    const timeSlots = useMemo(() => {
        const slots = [];
        for (
            let mins = HOUR_START * 60;
            mins < HOUR_END * 60;
            mins += cellDuration
        ) {
            const h = Math.floor(mins / 60);
            const m = mins % 60;
            const label = format(
                new Date(2000, 0, 1, h, m),
                h >= 12 ? "h:mm a" : "h:mm a"
            );
            const key = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
            slots.push({ label, key, mins });
        }
        return slots;
    }, [cellDuration]);

    const totalRows = timeSlots.length;
    const totalCols = days.length * providers.length;

    // Build availability sets per provider (memoized)
    const availabilitySets = useMemo(() => {
        const sets = {};
        if (!calendarData?.providers) return sets;

        for (const provider of providers) {
            const providerData = calendarData.providers[provider.id];
            sets[provider.id] = buildAvailabilitySet(
                providerData?.availability,
                cellDuration
            );
        }
        return sets;
    }, [calendarData, providers, cellDuration]);

    // Group events by provider+date, separating all-day from timed events
    const { timedEvents, allDayEvents } = useMemo(() => {
        const timed = {};
        const allDay = {};
        if (!calendarData?.providers) return { timedEvents: timed, allDayEvents: allDay };

        for (const provider of providers) {
            const providerData = calendarData.providers[provider.id];
            if (!providerData) continue;

            for (const bk of providerData.bookings || []) {
                const dateKey = bk.date || bk.startDate;
                const key = `${provider.id}|${dateKey}`;
                if (isAllDayEvent(bk)) {
                    if (!allDay[key]) allDay[key] = [];
                    allDay[key].push(bk);
                } else {
                    if (!timed[key]) timed[key] = { bookings: [], blockedTimes: [] };
                    timed[key].bookings.push(bk);
                }
            }

            for (const bt of providerData.blockedTimes || []) {
                const key = `${provider.id}|${bt.date}`;
                if (isAllDayEvent(bt)) {
                    if (!allDay[key]) allDay[key] = [];
                    allDay[key].push({ ...bt, _isBlocked: true });
                } else {
                    if (!timed[key]) timed[key] = { bookings: [], blockedTimes: [] };
                    timed[key].blockedTimes.push(bt);
                }
            }
        }
        return { timedEvents: timed, allDayEvents: allDay };
    }, [calendarData, providers]);

    // Check if there are any all-day events to show the bar
    const hasAllDayEvents = Object.keys(allDayEvents).length > 0;

    const [contextMenu, setContextMenu] = useState(null);

    const handleEventClick = useCallback((event, type, e) => {
        clearTimeout(hoverTimerRef.current);
        setTooltipState({ anchorEl: e.currentTarget, event, type, pinned: true });
    }, []);

    const handleTooltipClose = useCallback(() => {
        clearTimeout(hoverTimerRef.current);
        setTooltipState({ anchorEl: null, event: null, type: null, pinned: false });
    }, []);

    const handleEventMouseEnter = useCallback((event, type, e) => {
        clearTimeout(hoverTimerRef.current);
        const target = e.currentTarget;
        setTooltipState((prev) =>
            prev.pinned ? prev : { anchorEl: target, event, type, pinned: false }
        );
    }, []);

    const handleEventMouseLeave = useCallback(() => {
        clearTimeout(hoverTimerRef.current);
        hoverTimerRef.current = setTimeout(() => {
            setTooltipState((prev) =>
                prev.pinned ? prev : { anchorEl: null, event: null, type: null, pinned: false }
            );
        }, 150);
    }, []);

    const handleCellClick = useCallback(
        (providerId, date, timeKey) => {
            onCellClick?.(providerId, date, timeKey);
        },
        [onCellClick]
    );

    const handleContextMenu = useCallback((e, providerId, dateStr, timeKey) => {
        e.preventDefault();
        if (!canAddBooking) return;
        setContextMenu({
            mouseX: e.clientX,
            mouseY: e.clientY,
            providerId,
            dateStr,
            timeKey
        });
    }, []);

    const handleContextMenuClose = useCallback(() => {
        setContextMenu(null);
    }, []);

    const handleContextMenuBook = useCallback(() => {
        if (contextMenu) {
            onCellClick?.(contextMenu.providerId, contextMenu.dateStr, contextMenu.timeKey);
        }
        setContextMenu(null);
    }, [contextMenu, onCellClick]);

    return (
        <Box ref={scrollContainerRef} sx={{ position: "relative", overflow: "auto", flex: 1 }}>
            {/* Header row: provider names + day labels */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: `${TIME_LABEL_WIDTH}px repeat(${totalCols}, minmax(100px, 1fr))`,
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    backgroundColor: "#fff",
                    borderBottom: "2px solid #e0e0e0"
                }}
            >
                {/* Empty top-left corner */}
                <Box
                    sx={{
                        gridColumn: 1,
                        gridRow: "1 / 4",
                        borderRight: "1px solid #e0e0e0"
                    }}
                />

                {/* Provider name headers (row 1) */}
                {providers.map((provider, pIdx) => (
                    <Box
                        key={provider.id}
                        sx={{
                            gridColumn: `${pIdx * days.length + 2} / ${(pIdx + 1) * days.length + 2}`,
                            gridRow: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            height: PROVIDER_NAME_HEIGHT,
                            borderRight:
                                pIdx < providers.length - 1
                                    ? "2px solid #e0e0e0"
                                    : "none",
                            backgroundColor: "#fafafa"
                        }}
                    >
                        <Typography
                            variant="subtitle2"
                            fontWeight={600}
                            noWrap
                            sx={{ px: 1 }}
                        >
                            {provider.firstname} {provider.lastname}
                        </Typography>
                    </Box>
                ))}

                {/* Timezone strip per provider (row 2) */}
                {providers.map((provider, pIdx) => {
                    const colors =
                        TZ_STRIP_COLORS[pIdx % TZ_STRIP_COLORS.length];
                    return (
                        <Box
                            key={`tz-${provider.id}`}
                            sx={{
                                gridColumn: `${pIdx * days.length + 2} / ${(pIdx + 1) * days.length + 2}`,
                                gridRow: 2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                height: TZ_STRIP_HEIGHT,
                                backgroundColor: colors.bg,
                                borderBottom: `1px solid ${colors.border}`,
                                borderRight:
                                    pIdx < providers.length - 1
                                        ? "2px solid #e0e0e0"
                                        : "none",
                                gap: 0.5,
                                px: 1
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    fontSize: "0.65rem",
                                    fontWeight: 600,
                                    color: colors.text,
                                    whiteSpace: "nowrap"
                                }}
                            >
                                {formatTimezoneDisplay(provider.timezone)}
                            </Typography>
                        </Box>
                    );
                })}

                {/* Day headers under each provider (row 3) */}
                {providers.map((provider, pIdx) =>
                    days.map((day, dIdx) => {
                        const colIndex = pIdx * days.length + dIdx + 2;
                        const isToday = isSameDay(day, new Date());
                        return (
                            <Box
                                key={`${provider.id}-${dIdx}`}
                                sx={{
                                    gridColumn: colIndex,
                                    gridRow: 3,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    height: DAY_HEADER_HEIGHT,
                                    borderRight: "1px solid #e0e0e0",
                                    backgroundColor: isToday
                                        ? "#E3F2FD"
                                        : "#fafafa"
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    fontWeight={isToday ? 700 : 400}
                                    color={
                                        isToday
                                            ? "primary.main"
                                            : "text.secondary"
                                    }
                                >
                                    {format(day, "EEE M/d")}
                                </Typography>
                            </Box>
                        );
                    })
                )}
            </Box>

            {/* All-day events bar */}
            {hasAllDayEvents && (
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: `${TIME_LABEL_WIDTH}px repeat(${totalCols}, minmax(100px, 1fr))`,
                        borderBottom: "2px solid #e0e0e0",
                        backgroundColor: "#fafafa",
                        position: "sticky",
                        top: HEADER_HEIGHT,
                        zIndex: 9
                    }}
                >
                    {/* Label */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            pr: 1,
                            borderRight: "1px solid #e0e0e0",
                            py: 0.5
                        }}
                    >
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: "0.65rem" }}
                        >
                            all-day
                        </Typography>
                    </Box>

                    {/* All-day event cells */}
                    {providers.map((provider, pIdx) =>
                        days.map((day, dIdx) => {
                            const dateStr = format(day, "yyyy-MM-dd");
                            const colIndex = pIdx * days.length + dIdx + 2;
                            const key = `${provider.id}|${dateStr}`;
                            const events = allDayEvents[key] || [];

                            return (
                                <Box
                                    key={`allday-${colIndex}`}
                                    sx={{
                                        gridColumn: colIndex,
                                        borderRight: "1px solid #f0f0f0",
                                        py: 0.5,
                                        px: "2px",
                                        minHeight: 28
                                    }}
                                >
                                    {events.map((evt, idx) => (
                                        <Box
                                            key={idx}
                                            onClick={(e) =>
                                                handleEventClick(
                                                    evt,
                                                    evt._isBlocked
                                                        ? "blocked"
                                                        : "booking",
                                                    e
                                                )
                                            }
                                            sx={{
                                                backgroundColor: evt._isBlocked
                                                    ? "#9E9E9E"
                                                    : evt.source === "googleCal"
                                                      ? "#e0e0e0"
                                                      : evt.source === "course"
                                                        ? "#2E7D32"
                                                        : "#0087EE",
                                                ...(evt.source === "googleCal" && {
                                                    backgroundImage:
                                                        "repeating-linear-gradient(135deg, transparent, transparent 3px, rgba(0,0,0,0.06) 3px, rgba(0,0,0,0.06) 6px)"
                                                }),
                                                color: evt.source === "googleCal"
                                                    ? "rgba(0,0,0,0.8)"
                                                    : "#fff",
                                                border: evt.source === "googleCal"
                                                    ? "1px solid rgba(0,0,0,0.15)"
                                                    : "none",
                                                borderRadius: "3px",
                                                px: 0.75,
                                                py: "1px",
                                                mb: "2px",
                                                fontSize: "0.65rem",
                                                fontWeight: 500,
                                                cursor: "pointer",
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                "&:hover": {
                                                    opacity: 0.85
                                                }
                                            }}
                                        >
                                            {evt._isBlocked
                                                ? evt.notes || "Blocked"
                                                : evt.clientName ||
                                                  evt.servicetypeName ||
                                                  "All day"}
                                        </Box>
                                    ))}
                                </Box>
                            );
                        })
                    )}
                </Box>
            )}

            {/* Grid body: time slots */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: `${TIME_LABEL_WIDTH}px repeat(${totalCols}, minmax(100px, 1fr))`,
                    gridTemplateRows: `repeat(${totalRows}, ${cellHeight}px)`,
                    position: "relative"
                }}
            >
                {/* Current time indicator */}
                <CurrentTimeIndicator
                    cellHeight={cellHeight}
                    cellDuration={cellDuration}
                    totalCols={totalCols}
                />
                {/* Time labels */}
                {timeSlots.map((slot, rowIdx) => (
                    <Box
                        key={`time-${slot.key}`}
                        sx={{
                            gridColumn: 1,
                            gridRow: rowIdx + 1,
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "flex-end",
                            pr: 1,
                            pt: "2px",
                            borderRight: "1px solid #e0e0e0",
                            borderTop:
                                rowIdx > 0 ? "1px solid #f0f0f0" : "none"
                        }}
                    >
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: "0.65rem", lineHeight: 1 }}
                        >
                            {slot.label}
                        </Typography>
                    </Box>
                ))}

                {/* Calendar cells */}
                {providers.map((provider, pIdx) =>
                    days.map((day, dIdx) => {
                        const dateStr = format(day, "yyyy-MM-dd");
                        const colIndex = pIdx * days.length + dIdx + 2;
                        const isLastProviderCol =
                            dIdx === days.length - 1 &&
                            pIdx < providers.length - 1;

                        return (
                            <CalendarColumn
                                key={`${provider.id}-${dateStr}`}
                                provider={provider}
                                dateStr={dateStr}
                                colIndex={colIndex}
                                timeSlots={timeSlots}
                                cellHeight={cellHeight}
                                cellDuration={cellDuration}
                                availabilitySet={
                                    availabilitySets[provider.id]
                                }
                                events={
                                    timedEvents[
                                        `${provider.id}|${dateStr}`
                                    ]
                                }
                                isLastProviderCol={isLastProviderCol}
                                onCellClick={handleCellClick}
                                onContextMenu={handleContextMenu}
                                onEventClick={handleEventClick}
                                onEventMouseEnter={handleEventMouseEnter}
                                onEventMouseLeave={handleEventMouseLeave}
                                canAddBooking={canAddBooking}
                            />
                        );
                    })
                )}
            </Box>

            {/* Tooltip popover */}
            <CalendarTooltip
                anchorEl={tooltipState.anchorEl}
                event={tooltipState.event}
                type={tooltipState.type}
                pinned={tooltipState.pinned}
                onClose={handleTooltipClose}
                onEdit={(event, type) => {
                    handleTooltipClose();
                    onEventEdit?.(event, type);
                }}
                onDelete={(event, type) => {
                    handleTooltipClose();
                    onEventDelete?.(event, type);
                }}
                onMouseEnter={() => clearTimeout(hoverTimerRef.current)}
                onMouseLeave={handleEventMouseLeave}
            />

            {/* Right-click context menu */}
            <Menu
                open={contextMenu !== null}
                onClose={handleContextMenuClose}
                anchorReference="anchorPosition"
                anchorPosition={
                    contextMenu
                        ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                        : undefined
                }
            >
                <MenuItem onClick={handleContextMenuBook}>
                    <ListItemIcon>
                        <AddIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>
                        New booking at {contextMenu?.timeKey || ""}
                    </ListItemText>
                </MenuItem>
            </Menu>
        </Box>
    );
});

/**
 * CalendarColumn - renders cells + events for one provider+day column.
 * Separated for memoization.
 */
const CalendarColumn = React.memo(function CalendarColumn({
    provider,
    dateStr,
    colIndex,
    timeSlots,
    cellHeight,
    cellDuration,
    availabilitySet,
    events,
    isLastProviderCol,
    onCellClick,
    onContextMenu,
    onEventClick,
    onEventMouseEnter,
    onEventMouseLeave,
    canAddBooking = true
}) {
    return (
        <>
            {/* Background cells */}
            {timeSlots.map((slot, rowIdx) => {
                const isAvailable = availabilitySet?.has(
                    `${dateStr}|${slot.key}`
                );
                return (
                    <Box
                        key={`cell-${colIndex}-${rowIdx}`}
                        onClick={() =>
                            onCellClick(provider.id, dateStr, slot.key)
                        }
                        onContextMenu={(e) =>
                            onContextMenu(e, provider.id, dateStr, slot.key)
                        }
                        sx={{
                            gridColumn: colIndex,
                            gridRow: rowIdx + 1,
                            backgroundColor: isAvailable
                                ? "#fff"
                                : "rgba(0,0,0,0.12)",
                            borderTop:
                                rowIdx > 0 ? "1px solid #f0f0f0" : "none",
                            borderRight: isLastProviderCol
                                ? "2px solid #e0e0e0"
                                : "1px solid #f0f0f0",
                            cursor: canAddBooking ? "pointer" : "default",
                            position: "relative",
                            transition: "background-color 0.1s",
                            "&:hover": canAddBooking
                                ? {
                                      backgroundColor: isAvailable
                                          ? "rgba(0, 135, 238, 0.1)"
                                          : "rgba(0,0,0,0.16)"
                                  }
                                : {}
                        }}
                    />
                );
            })}

            {/* Event overlays - positioned absolutely with overlap layout */}
            {events && (
                <Box
                    sx={{
                        gridColumn: colIndex,
                        gridRow: `1 / ${timeSlots.length + 1}`,
                        position: "relative",
                        pointerEvents: "none",
                        zIndex: 3
                    }}
                >
                    <Box
                        sx={{
                            position: "relative",
                            height: "100%",
                            pointerEvents: "none"
                        }}
                    >
                        {(() => {
                            // Combine all events with their type, then layout together
                            const allEvents = [
                                ...(events.bookings || []).map((e) => ({ evt: e, type: "booking" })),
                                ...(events.blockedTimes || []).map((e) => ({ evt: e, type: "blocked" }))
                            ];
                            const allRaw = allEvents.map((e) => e.evt);
                            const layoutItems = layoutOverlappingEvents(allRaw, cellDuration, cellHeight);

                            return layoutItems.map((item, idx) => {
                                const typeInfo = allEvents.find((e) => e.evt === item.evt);
                                const evtType = typeInfo?.type || "booking";
                                const padding = 1; // px gap between side-by-side events
                                const sliverPct = 10; // reserve 10% on the right for cell click-through
                                const usablePct = 100 - sliverPct;
                                const colWidthPct = usablePct / item.totalCols;
                                const leftPct = item.col * colWidthPct;

                                return (
                                    <CalendarEvent
                                        key={`evt-${item.evt.id || idx}-${idx}`}
                                        event={item.evt}
                                        type={evtType}
                                        topOffset={item.pos.topOffset}
                                        height={item.pos.height}
                                        colLeft={`calc(${leftPct}% + ${padding}px)`}
                                        colWidth={`calc(${colWidthPct}% - ${padding * 2}px)`}
                                        onClick={onEventClick}
                                        onContextMenu={(event, type, e) => {
                                            const timeKey = event.startTime || "00:00";
                                            onContextMenu(e, provider.id, dateStr, timeKey);
                                        }}
                                        onMouseEnter={onEventMouseEnter}
                                        onMouseLeave={onEventMouseLeave}
                                    />
                                );
                            });
                        })()}
                    </Box>
                </Box>
            )}
        </>
    );
});

/**
 * CurrentTimeIndicator - red line showing the current time across the grid.
 * Updates every 60 seconds.
 */
const CurrentTimeIndicator = React.memo(function CurrentTimeIndicator({
    cellHeight,
    cellDuration,
    totalCols
}) {
    const [now, setNow] = useState(new Date());

    useEffect(() => {
        const interval = setInterval(() => setNow(new Date()), 60000);
        return () => clearInterval(interval);
    }, []);

    const currentMins = now.getHours() * 60 + now.getMinutes();
    const pixelsPerMin = cellHeight / cellDuration;
    const topOffset = (currentMins - HOUR_START * 60) * pixelsPerMin;

    // Don't render if outside visible range
    if (topOffset < 0 || currentMins >= HOUR_END * 60) return null;

    return (
        <Box
            sx={{
                position: "absolute",
                top: `${topOffset}px`,
                left: `${TIME_LABEL_WIDTH}px`,
                right: 0,
                zIndex: 4,
                pointerEvents: "none",
                display: "flex",
                alignItems: "center"
            }}
        >
            {/* Red dot */}
            <Box
                sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: "#EA4335",
                    marginLeft: "-5px",
                    flexShrink: 0
                }}
            />
            {/* Red line */}
            <Box
                sx={{
                    height: "2px",
                    backgroundColor: "#EA4335",
                    flex: 1
                }}
            />
        </Box>
    );
});

export default CalendarGrid;
